import React, { useState, useEffect } from 'react';
import {
    Button, Card, message, Typography, Space, Divider, Modal, Tag, InputNumber, Select, TimePicker, Switch
} from 'antd';
import {
    SyncOutlined, DatabaseOutlined, ExclamationCircleOutlined, CheckCircleOutlined,
    SettingOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { syncService, PortalTableDto } from '../services/syncService';
import { rulesService } from '../services/rulesService';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const ChungTab: React.FC = () => {
    const [syncLoading, setSyncLoading] = useState(false);
    const [portalTables, setPortalTables] = useState<PortalTableDto[]>([]);
    const [tablesLoading, setTablesLoading] = useState(false);
    const [selectedTable, setSelectedTable] = useState<string | undefined>();
    const [copyLoading, setCopyLoading] = useState(false);

    // Config cooldown
    const [cooldownMinutes, setCooldownMinutes] = useState<number>(10);
    const [cooldownLoading, setCooldownLoading] = useState(false);

    // Config auto-run rules
    const [autoRunEnabled, setAutoRunEnabled] = useState<boolean>(false);
    const [autoRunStartTime, setAutoRunStartTime] = useState<Dayjs>(dayjs().hour(8).minute(0));
    const [autoRunEndTime, setAutoRunEndTime] = useState<Dayjs>(dayjs().hour(17).minute(0));
    const [autoRunInterval, setAutoRunInterval] = useState<number>(60);
    const [autoRunLoading, setAutoRunLoading] = useState(false);

    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        tableName: string;
        rowCount: number;
    }>({ open: false, tableName: '', rowCount: 0 });

    useEffect(() => {
        loadPortalTables();
        loadCooldownConfig();
        loadAutoRunConfig();
    }, []);

    const loadCooldownConfig = async () => {
        try {
            const value = await rulesService.getConfig('rules_cooldown_minutes');
            if (value) {
                setCooldownMinutes(parseInt(value, 10));
            }
        } catch (error: any) {
            console.error('Load cooldown config error:', error);
        }
    };

    const handleSaveCooldown = async () => {
        setCooldownLoading(true);
        try {
            await rulesService.upsertConfig('rules_cooldown_minutes', cooldownMinutes.toString(), 'Thời gian cooldown (phút) cho việc chạy tất cả rules');
            message.success('Cập nhật cấu hình cooldown thành công');
        } catch (error: any) {
            console.error('Save cooldown config error:', error);
            message.error('Cập nhật cấu hình cooldown thất bại');
        } finally {
            setCooldownLoading(false);
        }
    };

    // Update autoRunInterval khi cooldownMinutes thay đổi để đảm bảo >= 1.5 * cooldown
    useEffect(() => {
        const minInterval = Math.ceil(cooldownMinutes * 1.5);
        if (autoRunInterval < minInterval) {
            setAutoRunInterval(minInterval);
        }
    }, [cooldownMinutes]);

    const loadAutoRunConfig = async () => {
        try {
            const enabled = await rulesService.getConfig('rules_autorun_enabled');
            const startTime = await rulesService.getConfig('rules_autorun_start_time');
            const endTime = await rulesService.getConfig('rules_autorun_end_time');
            const interval = await rulesService.getConfig('rules_autorun_interval_minutes');

            if (enabled) setAutoRunEnabled(enabled === 'true');
            if (startTime) setAutoRunStartTime(dayjs(startTime, 'HH:mm'));
            if (endTime) setAutoRunEndTime(dayjs(endTime, 'HH:mm'));
            if (interval) setAutoRunInterval(parseInt(interval, 10));
        } catch (error: any) {
            console.error('Load auto-run config error:', error);
        }
    };

    const handleSaveAutoRun = async () => {
        // Validate: autoRunInterval phải >= 1.5 * cooldownMinutes
        const minInterval = Math.ceil(cooldownMinutes * 1.5);
        if (autoRunInterval < minInterval) {
            message.error(`Khoảng thời gian giữa các lần chạy phải ít nhất ${minInterval} phút (1.5 lần cooldown = ${cooldownMinutes} phút)`);
            return;
        }

        setAutoRunLoading(true);
        try {
            await rulesService.upsertConfig('rules_autorun_enabled', autoRunEnabled.toString(), 'Bật/tắt chạy tự động rules');
            await rulesService.upsertConfig('rules_autorun_start_time', autoRunStartTime.format('HH:mm'), 'Giờ bắt đầu chạy tự động');
            await rulesService.upsertConfig('rules_autorun_end_time', autoRunEndTime.format('HH:mm'), 'Giờ kết thúc chạy tự động');
            await rulesService.upsertConfig('rules_autorun_interval_minutes', autoRunInterval.toString(), 'Khoảng thời gian giữa các lần chạy (phút)');
            message.success('Cập nhật cấu hình auto-run thành công');
        } catch (error: any) {
            console.error('Save auto-run config error:', error);
            message.error('Cập nhật cấu hình auto-run thất bại');
        } finally {
            setAutoRunLoading(false);
        }
    };

    const loadPortalTables = async () => {
        setTablesLoading(true);
        try {
            const list = await syncService.getPortalTables();
            setPortalTables(list);
        } catch (error: any) {
            console.error('Lỗi tải danh sách bảng Portal:', error);
            message.error(error?.response?.data?.message || 'Không thể tải danh sách bảng Portal');
        } finally {
            setTablesLoading(false);
        }
    };

    const handleSyncEmployees = async () => {
        try {
            setSyncLoading(true);
            await syncService.syncHisEmployees();
            message.success('Đồng bộ dữ liệu nhân viên HIS thành công!');
        } catch (error: any) {
            console.error('HIS Sync Error:', error);
            message.error(error?.response?.data?.message || 'Đồng bộ dữ liệu nhân viên HIS thất bại!');
        } finally {
            setSyncLoading(false);
        }
    };

    const doCopyTable = async (tableName: string, overwrite: boolean) => {
        setCopyLoading(true);
        try {
            const result = await syncService.copyTableToHis(tableName, overwrite);
            if (result.wasOverwritten) {
                message.success(`Đã GHI ĐÈ bảng [${tableName}] lên HIS thành công (${result.rowsCopied} dòng).`);
            } else {
                message.success(`Đã TẠO MỚI bảng [${tableName}] trên HIS thành công (${result.rowsCopied} dòng).`);
            }
        } catch (error: any) {
            console.error('Copy table error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || `Sao chép bảng [${tableName}] thất bại!`;
            message.error(msg);
        } finally {
            setCopyLoading(false);
        }
    };

    const handleCopyTable = async () => {
        if (!selectedTable) {
            message.warning('Vui lòng chọn 1 bảng từ danh sách');
            return;
        }
        setCopyLoading(true);
        try {
            const info = await syncService.checkHisTable(selectedTable);
            if (info.exists) {
                setConfirmModal({
                    open: true,
                    tableName: info.tableName || selectedTable,
                    rowCount: Number(info.rowCount ?? 0)
                });
                setCopyLoading(false);
            } else {
                await doCopyTable(selectedTable, false);
            }
        } catch (error: any) {
            console.error('Check HIS table error:', error);
            const data = error?.response?.data || {};
            const msg = data.message || data.Message || error?.message || 'Lỗi kiểm tra bảng trên HIS';
            message.error(msg);
        } finally {
            if (!confirmModal.open) {
                setCopyLoading(false);
            }
        }
    };

    const closeConfirmModal = () => {
        setConfirmModal({ open: false, tableName: '', rowCount: 0 });
    };

    const handleConfirmOverwrite = async () => {
        const tbl = confirmModal.tableName;
        closeConfirmModal();
        if (!tbl) return;
        await doCopyTable(tbl, true);
    };

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card>
                    <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space direction="vertical" size={4}>
                            <Title level={4} style={{ margin: 0 }}>
                                <SyncOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                Đồng bộ dữ liệu nhân viên HIS
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                Đồng bộ danh sách nhân viên, khoa phòng, chức danh/vụ từ CSDL HIS (ReportDB) vào Portal
                            </Text>
                        </Space>
                        <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            onClick={handleSyncEmployees}
                            loading={syncLoading}
                        >
                            Đồng bộ nhân viên HIS
                        </Button>
                    </Space>
                </Card>

                <Divider style={{ margin: '4px 0' }} />

                <Card>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Space align="start" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <Space direction="vertical" size={4}>
                                <Title level={4} style={{ margin: 0 }}>
                                    <DatabaseOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                                    Sao chép bảng Portal → HIS (ReportDB)
                                </Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Lựa chọn 1 bảng danh mục/cấu hình từ Portal để copy sang CSDL HIS (ReportDB)
                                </Text>
                            </Space>
                            <Space>
                                <Button
                                    onClick={loadPortalTables}
                                    loading={tablesLoading}
                                >
                                    Làm mới danh sách
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<DatabaseOutlined />}
                                    onClick={handleCopyTable}
                                    loading={copyLoading}
                                    disabled={!selectedTable}
                                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                >
                                    Sao chép sang HIS
                                </Button>
                            </Space>
                        </Space>

                        <Space.Compact style={{ width: '100%' }}>
                            <Select
                                showSearch
                                allowClear
                                loading={tablesLoading}
                                placeholder="-- Chọn bảng từ Portal để sao chép sang HIS --"
                                style={{ width: '100%' }}
                                filterOption={(input, option: any) => {
                                    const name = (option?.value as string) || '';
                                    return name.toLowerCase().includes(input.toLowerCase());
                                }}
                                value={selectedTable}
                                onChange={setSelectedTable}
                                size="large"
                            >
                                {portalTables.map(t => (
                                    <Option key={t.tableName} value={t.tableName}>
                                        <Space>
                                            <Text strong>{t.tableName}</Text>
                                            <Tag color="blue">
                                                <CheckCircleOutlined /> {t.rowCount.toLocaleString('vi-VN')} dòng
                                            </Tag>
                                            {t.description && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>— {t.description}</Text>
                                            )}
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                        </Space.Compact>

                        {selectedTable && (
                            <Card
                                size="small"
                                type="inner"
                                style={{ backgroundColor: '#fafafa' }}
                                title={<Text style={{ fontSize: 13 }}>Thông tin bảng đã chọn</Text>}
                            >
                                {(() => {
                                    const t = portalTables.find(x => x.tableName === selectedTable);
                                    if (!t) return null;
                                    return (
                                        <Space direction="vertical" size={4} style={{ fontSize: 13 }}>
                                            <Text><strong>Tên bảng:</strong> <Tag color="blue">{t.tableName}</Tag></Text>
                                            {t.description && <Text><strong>Mô tả:</strong> {t.description}</Text>}
                                            <Text><strong>Số dòng trên Portal:</strong> <Tag color="green">{t.rowCount.toLocaleString('vi-VN')}</Tag></Text>
                                            <Text type="secondary">
                                                Lưu ý: Click "Sao chép sang HIS" sẽ tự kiểm tra bảng. Nếu đã tồn tại, hệ thống sẽ hỏi xác nhận trước khi xóa bảng cũ và ghi đè dữ liệu mới.
                                            </Text>
                                        </Space>
                                    );
                                })()}
                            </Card>
                        )}
                    </Space>
                </Card>

                <Divider style={{ margin: '4px 0' }} />

                <Card>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Space align="start" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <Space direction="vertical" size={4}>
                                <Title level={4} style={{ margin: 0 }}>
                                    <SettingOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                                    Cấu hình Cooldown Rules
                                </Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Thiết lập thời gian cooldown (phút) cho việc chạy tất cả rules cảnh báo
                                </Text>
                            </Space>
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={handleSaveCooldown}
                                loading={cooldownLoading}
                                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                            >
                                Lưu cấu hình
                            </Button>
                        </Space>

                        <Space align="center" size={12}>
                            <Text strong>Thời gian cooldown:</Text>
                            <InputNumber
                                min={1}
                                max={1440}
                                value={cooldownMinutes}
                                onChange={(value) => setCooldownMinutes(value || 10)}
                                style={{ width: 120 }}
                            />
                            <Text>phút</Text>
                        </Space>
                    </Space>
                </Card>

                <Divider style={{ margin: '4px 0' }} />

                <Card>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Space align="start" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <Space direction="vertical" size={4}>
                                <Title level={4} style={{ margin: 0 }}>
                                    <ClockCircleOutlined style={{ marginRight: 8, color: '#eb2f96' }} />
                                    Cấu hình Auto-run Rules
                                </Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Thiết lập chạy tự động các rules active trong khoảng thời gian nhất định
                                </Text>
                            </Space>
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={handleSaveAutoRun}
                                loading={autoRunLoading}
                                style={{ backgroundColor: '#eb2f96', borderColor: '#eb2f96' }}
                            >
                                Lưu cấu hình
                            </Button>
                        </Space>

                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Space align="center">
                                <Switch
                                    checked={autoRunEnabled}
                                    onChange={setAutoRunEnabled}
                                    checkedChildren="Bật"
                                    unCheckedChildren="Tắt"
                                />
                                <Text type="secondary">Bật/tắt chạy tự động</Text>
                            </Space>

                            <Space align="center" size={12}>
                                <Text strong>Từ:</Text>
                                <TimePicker
                                    value={autoRunStartTime}
                                    onChange={(value) => value && setAutoRunStartTime(value)}
                                    format="HH:mm"
                                    style={{ width: 120 }}
                                    disabled={!autoRunEnabled}
                                />
                                <Text strong>đến:</Text>
                                <TimePicker
                                    value={autoRunEndTime}
                                    onChange={(value) => value && setAutoRunEndTime(value)}
                                    format="HH:mm"
                                    style={{ width: 120 }}
                                    disabled={!autoRunEnabled}
                                />
                            </Space>

                            <Space align="center" size={12}>
                                <Text strong>Khoảng cách:</Text>
                                <InputNumber
                                    min={Math.ceil(cooldownMinutes * 1.5)}
                                    max={1440}
                                    value={autoRunInterval}
                                    onChange={(value) => setAutoRunInterval(value || Math.ceil(cooldownMinutes * 1.5))}
                                    style={{ width: 120 }}
                                    disabled={!autoRunEnabled}
                                />
                                <Text>phút</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>(tối thiểu {Math.ceil(cooldownMinutes * 1.5)} phút = 1.5 × cooldown)</Text>
                            </Space>
                        </Space>
                    </Space>
                </Card>
            </Space>

            {/* Modal xác nhận ghi đè bảng trên HIS */}
            <Modal
                open={confirmModal.open}
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        Bảng đã tồn tại trên HIS
                    </Space>
                }
                okText="Đồng ý, Ghi đè"
                okType="danger"
                cancelText="Hủy"
                confirmLoading={copyLoading}
                onCancel={closeConfirmModal}
                onOk={handleConfirmOverwrite}
                destroyOnClose
            >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <Text>
                        Bảng <Tag color="blue">[{confirmModal.tableName}]</Tag> đã có trên CSDL HIS
                    </Text>
                    <Text type="warning">
                        Số dòng hiện tại: <strong>{Number(confirmModal.rowCount ?? 0).toLocaleString('vi-VN')}</strong>
                    </Text>
                    <Text type="danger">
                        Nếu tiếp tục, bảng cũ sẽ bị <strong>XÓA HOÀN TOÀN</strong> và thay bằng dữ liệu mới từ Portal.
                    </Text>
                </Space>
            </Modal>
        </>
    );
};

export default ChungTab;
